/**
 * Pure scheduling logic for the background printer auto-reconnect loop
 * (`usePrinterAutoReconnect`). Kept free of React/store/plugin imports so the
 * timing rules are unit-testable in isolation.
 */

/** How long the printer must stay unreachable before the first reconnect attempt. */
export const RECONNECT_GRACE_MS = 120_000;

/** Delay after each failed attempt; the last step repeats indefinitely. */
export const RECONNECT_BACKOFF_MS = [30_000, 60_000, 120_000, 300_000];

/** Re-check delay when a tick lands while a print job is in flight. */
export const RECONNECT_PRINTING_DEFER_MS = 10_000;

/**
 * Minimum delay before an attempt on return to foreground — lets PrintingInit's
 * own resume readiness check finish first so single-flight doesn't swallow the
 * forced reconnect attempt.
 */
export const RECONNECT_RESUME_DELAY_MS = 2_000;

/**
 * Delay before the next attempt given how many attempts have failed since the
 * printer went unreachable (>= 1). Caps at the last backoff step.
 */
export function getBackoffDelayMs(failedAttempts: number): number {
  const index = Math.min(Math.max(failedAttempts, 1), RECONNECT_BACKOFF_MS.length) - 1;
  return RECONNECT_BACKOFF_MS[index];
}

/**
 * Whether the saved printer is unreachable (connection-level problem), as
 * opposed to reachable-but-degraded (paper empty / cover open while online —
 * reconnecting can't fix those, so the loop must not run for them).
 *
 * `status === null` counts as unreachable because a probe that throws before
 * any status was ever read (e.g. cold boot with the printer powered off)
 * leaves the store without one.
 */
export function isPrinterUnreachable(state: {
  activePrinter: unknown | null;
  isPrinterReady: boolean;
  status: { offline?: boolean } | null;
}): boolean {
  if (!state.activePrinter || state.isPrinterReady) return false;
  return state.status === null || state.status.offline === true;
}
